import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosInstance';
import { escapeHtml } from '../utils/escapeHtml';

const UnifiedInvoiceManager = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'manual', 'subscription'

  useEffect(() => {
    fetchAllInvoices();
    
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) {
      contentWrapper.style.marginLeft = '0';
      contentWrapper.style.marginRight = '0';
    }
    
    return () => {
      if (contentWrapper) {
        contentWrapper.style.marginLeft = '';
        contentWrapper.style.marginRight = '';
      }
    };
  }, []);

  const fetchAllInvoices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const businessUserId = localStorage.getItem('business_id');
      
      if (!businessUserId) {
        setError('User ID not found');
        return;
      }

      // Fetch both manual and subscription invoices in parallel
      const [manualResponse, subscriptionResponse] = await Promise.all([
        api.get('plan/invoices/list', {
          params: { business_user_id: businessUserId }
        }).catch(() => ({ data: { status: 'error', result: { invoices: [] } } })),
        
        api.get('subscription/invoices/list', {
          params: { business_user_id: businessUserId }
        }).catch(() => ({ data: { status: 'error', result: { invoices: [] } } }))
      ]);


      // Process manual invoices
      const manualInvoices = (manualResponse.data.status === 'success' 
        ? manualResponse.data.result?.invoices || [] 
        : []).map(inv => ({
        ...inv,
        invoice_type: 'manual',
        invoice_id: inv.order_id,
        display_type: 'Manual Purchase'
      }));

      // Process subscription invoices
      const subscriptionInvoices = (subscriptionResponse.data.status === 'success'
        ? subscriptionResponse.data.result?.invoices || []
        : []).map(inv => ({
        ...inv,
        invoice_type: 'subscription',
        invoice_id: inv.payment_id,
        display_type: 'Subscription'
      }));

      // Combine and sort by date (newest first)
      const allInvoices = [...manualInvoices, ...subscriptionInvoices]
        .sort((a, b) => new Date(b.paid_date) - new Date(a.paid_date));

      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      const endpoint = invoice.invoice_type === 'manual' 
        ? 'plan/invoice/download'
        : 'subscription/invoice/download';
      
      const params = invoice.invoice_type === 'manual'
        ? { order_id: invoice.order_id }
        : { payment_id: invoice.payment_id };

      const response = await api.get(endpoint, {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    }
  };


  const viewInvoice = async (invoice) => {
    try {
      const endpoint = invoice.invoice_type === 'manual'
        ? 'plan/invoice/download'
        : 'subscription/invoice/download';
      
      const params = invoice.invoice_type === 'manual'
        ? { order_id: invoice.order_id, format: 'base64' }
        : { payment_id: invoice.payment_id, format: 'base64' };

      const response = await api.get(endpoint, { params });

      if (response.data.status === 'success') {
        const pdfBase64 = response.data.result.pdf_base64;
        const invoiceNumber = response.data.result.invoice_number || 'Invoice';
        
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${escapeHtml(invoiceNumber)}</title>
                <style>
                  body { margin: 0; padding: 0; overflow: hidden; }
                  iframe { border: none; width: 100vw; height: 100vh; }
                </style>
              </head>
              <body>
                <iframe src="data:application/pdf;base64,${escapeHtml(pdfBase64)}" type="application/pdf"></iframe>
              </body>
            </html>
          `);
          newWindow.document.close();
        }
      } else {
        alert('Failed to load invoice');
      }
    } catch (error) {
      console.error('Error viewing invoice:', error);
      alert('Failed to load invoice. Please try again.');
    }
  };

  // Filter invoices based on active tab
  const getFilteredInvoices = () => {
    if (activeTab === 'manual') {
      return invoices.filter(inv => inv.invoice_type === 'manual');
    } else if (activeTab === 'subscription') {
      return invoices.filter(inv => inv.invoice_type === 'subscription');
    }
    return invoices; // 'all'
  };

  const filteredInvoices = getFilteredInvoices();

  // Count invoices by type
  const manualCount = invoices.filter(inv => inv.invoice_type === 'manual').length;
  const subscriptionCount = invoices.filter(inv => inv.invoice_type === 'subscription').length;


  return (
    <div className="content-wrapper" style={{ 
      minHeight: '100vh', 
      background: '#f4f6f9',
      marginLeft: '0 !important',
      position: 'relative'
    }}>
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: 9999,
          width: '45px',
          height: '45px',
          borderRadius: '50%',
          background: 'white',
          border: '2px solid #1E5B3A',
          color: '#1E5B3A',
          fontSize: '18px',
          cursor: 'pointer',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#1E5B3A';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.color = '#1E5B3A';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <i className="fas fa-arrow-left"></i>
      </button>

      {/* Header Section */}
      <section className="content-header" style={{ 
        background: 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
        padding: '25px 20px',
        marginBottom: '20px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        marginLeft: '0',
        marginRight: '0'
      }}>
        <div style={{ maxWidth: '100%', padding: '0 10px' }}>
          <div className="row align-items-center">
            <div className="col-md-12 col-12 mb-3 mb-md-0">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginLeft: '60px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)'
                }}>
                  <i className="fas fa-file-invoice" style={{ 
                    fontSize: '28px', 
                    color: 'white'
                  }}></i>
                </div>
                <div>
                  <h1 style={{ 
                    color: 'white', 
                    margin: 0, 
                    fontSize: '28px',
                    fontWeight: '700',
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}>
                    My Invoices
                  </h1>
                  <p style={{ 
                    color: 'rgba(255,255,255,0.9)', 
                    margin: 0,
                    fontSize: '14px'
                  }}>
                    View and download all your payment invoices
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Tab Navigation */}
      <section className="content" style={{ padding: '0 20px 20px 20px' }}>
        <div style={{ maxWidth: '100%', padding: '0 10px' }}>
          <div style={{
            // background: 'white',
            // borderRadius: '15px',
            // padding: '20px',
            // marginBottom: '20px',
            // boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {/* <button
                onClick={() => setActiveTab('all')}
                style={{
                  padding: '12px 30px',
                  borderRadius: '10px',
                  border: activeTab === 'all' ? '2px solid #1E5B3A' : '2px solid #e9ecef',
                  background: activeTab === 'all' ? '#1E5B3A' : 'white',
                  color: activeTab === 'all' ? 'white' : '#5B5040',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-list"></i>
                All Invoices
                <span style={{
                  background: activeTab === 'all' ? 'rgba(255,255,255,0.2)' : '#e9ecef',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {invoices.length}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('manual')}
                style={{
                  padding: '12px 30px',
                  borderRadius: '10px',
                  border: activeTab === 'manual' ? '2px solid #1E5B3A' : '2px solid #e9ecef',
                  background: activeTab === 'manual' ? '#1E5B3A' : 'white',
                  color: activeTab === 'manual' ? 'white' : '#5B5040',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-shopping-cart"></i>
                Manual Purchases
                <span style={{
                  background: activeTab === 'manual' ? 'rgba(255,255,255,0.2)' : '#e9ecef',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {manualCount}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('subscription')}
                style={{
                  padding: '12px 30px',
                  borderRadius: '10px',
                  border: activeTab === 'subscription' ? '2px solid #2E7D4F' : '2px solid #e9ecef',
                  background: activeTab === 'subscription' ? '#2E7D4F' : 'white',
                  color: activeTab === 'subscription' ? 'white' : '#5B5040',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-sync-alt"></i>
                Subscriptions
                <span style={{
                  background: activeTab === 'subscription' ? 'rgba(255,255,255,0.2)' : '#e9ecef',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  {subscriptionCount}
                </span>
              </button> */}
            </div>
          </div>


          {/* Loading State */}
          {loading ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              background: 'white',
              borderRadius: '15px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                <i className="fas fa-spinner fa-spin" style={{ 
                  fontSize: '35px', 
                  color: 'white' 
                }}></i>
              </div>
              <h4 style={{ color: '#1E5B3A', fontWeight: '600', marginBottom: '8px' }}>
                Loading Invoices
              </h4>
              <p style={{ color: '#5B5040', margin: 0 }}>
                Please wait while we fetch your invoices...
              </p>
              <style>{`
                @keyframes pulse {
                  0%, 100% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.05); opacity: 0.8; }
                }
              `}</style>
            </div>
          ) : error ? (
            <div style={{
              background: 'white',
              borderRadius: '15px',
              padding: '60px 30px',
              textAlign: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#fee',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ 
                  fontSize: '35px', 
                  color: '#dc3545' 
                }}></i>
              </div>
              <h4 style={{ color: '#dc3545', marginBottom: '10px' }}>Error Loading Invoices</h4>
              <p style={{ color: '#5B5040', marginBottom: '20px' }}>{error}</p>
              <button 
                onClick={fetchAllInvoices}
                className="btn btn-primary"
                style={{
                  padding: '10px 30px',
                  borderRadius: '8px',
                  background: '#1E5B3A',
                  border: 'none'
                }}
              >
                <i className="fas fa-redo mr-2"></i>
                Try Again
              </button>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '15px',
              padding: '80px 30px',
              textAlign: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, #FDF9F2 0%, #E1EFE3 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 25px'
              }}>
                <i className="fas fa-file-invoice" style={{ 
                  fontSize: '50px', 
                  color: '#1E5B3A',
                  opacity: 0.6
                }}></i>
              </div>
              <h3 style={{ color: '#495057', marginBottom: '12px', fontWeight: '600' }}>
                {activeTab === 'all' ? 'No Invoices Yet' : 
                 activeTab === 'manual' ? 'No Manual Purchase Invoices' : 
                 'No Subscription Invoices'}
              </h3>
              <p style={{ color: '#5B5040', fontSize: '15px', maxWidth: '400px', margin: '0 auto' }}>
                {activeTab === 'all' ? 'You haven\'t made any purchases yet. Once you purchase a plan, your invoices will appear here.' :
                 activeTab === 'manual' ? 'You haven\'t made any manual plan purchases yet.' :
                 'You don\'t have any subscription payments yet.'}
              </p>
            </div>
          ) : (


            <div className="row" style={{ margin: '0 -10px' }}>
              {filteredInvoices.map((invoice) => (
                <div key={invoice.invoice_id} className="col-xl-4 col-lg-6 col-md-6 col-12" style={{ padding: '0 10px', marginBottom: '20px' }}>
                  <div style={{
                    background: 'white',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    border: '1px solid #e9ecef',
                    height: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
                  }}>
                    {/* Card Header */}
                    <div style={{
                      background: 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
                      padding: '20px 25px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: '13px', 
                          color: 'rgba(255,255,255,0.8)',
                          marginBottom: '5px',
                          fontWeight: '500'
                        }}>
                          Invoice Number
                        </div>
                        <div style={{ 
                          fontSize: '18px', 
                          color: 'white',
                          fontWeight: '700',
                          fontFamily: 'monospace',
                          letterSpacing: '0.5px'
                        }}>
                          {invoice.invoice_number}
                        </div>
                      </div>
                      <div style={{
                        background: 'rgba(255,255,255,0.2)',
                        padding: '8px 15px',
                        borderRadius: '20px',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <i className="fas fa-check-circle" style={{ 
                          color: 'white', 
                          marginRight: '6px' 
                        }}></i>
                        <span style={{ 
                          color: 'white', 
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          PAID
                        </span>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div style={{ padding: '25px' }}>
                      {/* Invoice Type Badge */}
                      <div style={{ marginBottom: '15px' }}>
                        <span style={{
                          display: 'inline-block',
                          background: invoice.invoice_type === 'manual' 
                            ? 'linear-gradient(135deg, #E1EFE3 0%, #bbdefb 100%)'
                            : 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: invoice.invoice_type === 'manual' 
                            ? '1px solid #90caf9'
                            : '1px solid #9CC7A8',
                          fontSize: '12px',
                          fontWeight: '700',
                          color: invoice.invoice_type === 'manual' ? '#1E5B3A' : '#2e7d32'
                        }}>
                          <i className={invoice.invoice_type === 'manual' ? 'fas fa-shopping-cart' : 'fas fa-sync-alt'} 
                             style={{ marginRight: '5px' }}></i>
                          {invoice.display_type}
                        </span>
                      </div>

                      {/* Plan Name */}
                      <div style={{ marginBottom: '20px' }}>
                        <div style={{
                          display: 'inline-block',
                          background: 'linear-gradient(135deg, #FDF9F2 0%, #E1EFE3 100%)',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid #bae6fd'
                        }}>
                          <i className={invoice.invoice_type === 'manual' ? 'fas fa-crown' : 'fas fa-sync-alt'} 
                             style={{ color: '#1E5B3A', marginRight: '8px' }}></i>
                          <span style={{ 
                            color: '#1E5B3A', 
                            fontWeight: '600',
                            fontSize: '15px'
                          }}>
                            {invoice.plan_name}
                          </span>
                        </div>
                      </div>


                      {/* Amount Section */}
                      <div style={{
                        background: '#FDF9F2',
                        padding: '20px',
                        borderRadius: '10px',
                        marginBottom: '20px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: invoice.discount_amount > 0 ? '12px' : '0'
                        }}>
                          <span style={{ 
                            color: '#5B5040', 
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            Amount Paid
                          </span>
                          <span style={{ 
                            fontSize: '24px', 
                            fontWeight: '700',
                            color: '#1E5B3A'
                          }}>
                            ₹ {invoice.amount.toFixed(2)}
                          </span>
                        </div>
                        
                        {invoice.discount_amount > 0 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingTop: '12px',
                            borderTop: '1px dashed #dee2e6'
                          }}>
                            <span style={{ 
                              color: '#2E7D4F', 
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              <i className="fas fa-tag mr-1"></i>
                              {invoice.is_first_payment ? 'First Payment Discount' : 'Discount Applied'}
                            </span>
                            <span style={{ 
                              fontSize: '16px', 
                              fontWeight: '700',
                              color: '#2E7D4F'
                            }}>
                              - ₹ {invoice.discount_amount.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Details Grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '15px',
                        marginBottom: '20px'
                      }}>
                        <div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#5B5040',
                            marginBottom: '5px',
                            fontWeight: '500'
                          }}>
                            Payment Date
                          </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: '#212529',
                            fontWeight: '600'
                          }}>
                            <i className="far fa-calendar-alt mr-1" style={{ color: '#1E5B3A' }}></i>
                            {new Date(invoice.paid_date).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                        </div>

                        <div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#5B5040',
                            marginBottom: '5px',
                            fontWeight: '500'
                          }}>
                            {invoice.invoice_type === 'manual' ? 'Order ID' : 'Payment Method'}
                          </div>
                          <div style={{ 
                            fontSize: invoice.invoice_type === 'manual' ? '11px' : '14px', 
                            color: '#212529',
                            fontWeight: '600',
                            fontFamily: invoice.invoice_type === 'manual' ? 'monospace' : 'inherit',
                            wordBreak: 'break-all'
                          }}>
                            {invoice.invoice_type === 'manual' ? (
                              invoice.order_id
                            ) : (
                              <>
                                <i className="fas fa-credit-card mr-1" style={{ color: '#1E5B3A' }}></i>
                                {invoice.payment_method || 'Auto-Debit'}
                              </>
                            )}
                          </div>
                        </div>
                      </div>


                      {/* Validity/Billing Period */}
                      <div style={{
                        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                        padding: '15px',
                        borderRadius: '10px',
                        marginBottom: '20px',
                        border: '1px solid #fed7aa'
                      }}>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#9a3412',
                          marginBottom: '8px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          <i className="fas fa-clock mr-1"></i>
                          {invoice.invoice_type === 'manual' ? 'Validity Period' : 'Billing Period'}
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#7c2d12',
                          fontWeight: '600'
                        }}>
                          {invoice.invoice_type === 'manual' ? (
                            <>
                              {new Date(invoice.start_date).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                              <span style={{ margin: '0 8px', color: '#9a3412' }}>→</span>
                              {new Date(invoice.expiry_date).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </>
                          ) : (
                            <>
                              {new Date(invoice.billing_start).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                              <span style={{ margin: '0 8px', color: '#9a3412' }}>→</span>
                              {new Date(invoice.billing_end).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </>
                          )}
                        </div>
                      </div>

                      {/* First Payment Badge for Subscriptions */}
                      {invoice.invoice_type === 'subscription' && invoice.is_first_payment && (
                        <div style={{
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          marginBottom: '20px',
                          border: '1px solid #fbbf24',
                          textAlign: 'center'
                        }}>
                          <i className="fas fa-star" style={{ color: '#d97706', marginRight: '6px' }}></i>
                          <span style={{ 
                            color: '#92400e', 
                            fontSize: '13px',
                            fontWeight: '600'
                          }}>
                            First Subscription Payment
                          </span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '10px',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => viewInvoice(invoice)}
                          style={{
                            flex: '1',
                            minWidth: '140px',
                            padding: '12px 20px',
                            background: 'white',
                            border: '2px solid #1E5B3A',
                            color: '#1E5B3A',
                            borderRadius: '10px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#1E5B3A';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.color = '#1E5B3A';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <i className="fas fa-eye"></i>
                          View
                        </button>
                        <button
                          onClick={() => downloadInvoice(invoice)}
                          style={{
                            flex: '1',
                            minWidth: '140px',
                            padding: '12px 20px',
                            background: 'linear-gradient(135deg, #1E5B3A 0%, #2E7D4F 100%)',
                            border: 'none',
                            color: 'white',
                            borderRadius: '10px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 10px rgba(7, 101, 52, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 15px rgba(7, 101, 52, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 10px rgba(7, 101, 52, 0.3)';
                          }}
                        >
                          <i className="fas fa-download"></i>
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default UnifiedInvoiceManager;
